import * as React from "react"
import { Send, Mail } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { useAuthStore } from "@/stores/auth-store"
import { createApiClient } from "@/services/api/apiClient"
import useOrgDataStore from "@/stores/org-store"

interface ContactOverlayContextValue {
  state: "open" | "closed"
  setState: (state: "open" | "closed") => void
  openWithData?: (initialData?: { type?: string; subject?: string }) => void
  initialData?: { type?: string; subject?: string } | null
}

const ContactOverlayContext = React.createContext<ContactOverlayContextValue | undefined>(undefined)

const useContactOverlay = () => {
  const context = React.useContext(ContactOverlayContext)
  if (!context) {
    throw new Error("useContactOverlay must be used within a ContactOverlayProvider")
  }
  return context
}

interface ContactOverlayProviderProps {
  children: React.ReactNode
  defaultState?: "open" | "closed"
}

const ContactOverlayProvider = ({
  children,
  defaultState = "closed",
}: ContactOverlayProviderProps) => {
  const [state, setState] = React.useState<"open" | "closed">(defaultState)
  const [initialData, setInitialData] = React.useState<{ type?: string; subject?: string } | null>(null)

  const openWithData = React.useCallback((data?: { type?: string; subject?: string }) => {
    setInitialData(data || null)
    setState("open")
  }, [])

  return (
    <ContactOverlayContext.Provider value={{ state, setState, openWithData, initialData }}>
      {children}
    </ContactOverlayContext.Provider>
  )
}

// Contact Form Component
function ContactForm({ onClose, initialData }: { onClose: () => void; initialData?: { type?: string; subject?: string } }) {
  const { user } = useAuthStore()
  const { selectedDistrict, selectedSchool } = useOrgDataStore()
  const [formState, setFormState] = React.useState<'init' | 'submitting' | 'success'>('init')
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: initialData?.subject || '',
    description: '',
    type: initialData?.type || '',
    priority: false
  })

  // Update form data when initialData changes
  React.useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        subject: initialData.subject || prev.subject,
        type: initialData.type || prev.type
      }))
    }
  }, [initialData])

  // Auto-fill user data when component mounts
  React.useEffect(() => {
    if (user) {
      const userName = user.attributes?.given_name || user.sub
      const userEmail = user.attributes?.email || user.sub
      
      setFormData(prev => ({
        ...prev,
        name: userName || '',
        email: userEmail || ''
      }))
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormState('submitting')

    try {
      const apiClient = createApiClient()
      
      const requestData = {
        name: formData.name || `${user?.attributes?.given_name || ''} ${user?.attributes?.family_name || ''}`.trim(),
        email: formData.email || user?.attributes?.email || '',
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority,
        type: formData.type,
        // Additional context data
        sub: user?.sub || '',
        district: selectedDistrict?.name || '',
        school: selectedSchool?.name || '',
        path: window.location.pathname + window.location.search,
      }

      await apiClient.post('/v2/admin/contact', requestData)

      setFormState('success')
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Contact form submission failed:', error)
      setFormState('init')
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (formState === 'success') {
    return (
      <div className="text-center py-8">
        <Mail className="h-12 w-12 text-green-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Message Sent!</h3>
        <p className="text-muted-foreground">We received your message. We'll get back to you shortly.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Type of Request */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Type of Request</Label>
          <RadioGroup 
            value={formData.type} 
            onValueChange={(value) => handleInputChange('type', value)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Problem" id="type-problem" />
              <Label htmlFor="type-problem" className="text-sm">Help with using the platform</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Feature Request" id="type-feature" />
              <Label htmlFor="type-feature" className="text-sm">Feedback / Suggestion</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Question" id="type-question" />
              <Label htmlFor="type-question" className="text-sm">Business inquiry</Label>
            </div>
          </RadioGroup>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="priority" 
              checked={formData.priority}
              onCheckedChange={(checked) => handleInputChange('priority', checked as boolean)}
            />
            <Label htmlFor="priority" className="text-sm">Treat as urgent</Label>
          </div>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
          <Input
            id="subject"
            value={formData.subject}
            onChange={(e) => handleInputChange('subject', e.target.value)}
            placeholder="One line short description"
            required
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description" className="text-sm font-medium">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Provide as much detail as possible."
            rows={4}
            required
          />
        </div>

        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Your full name"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your.email@example.com"
          />
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-2">
          <Button 
            type="submit" 
            disabled={formState === 'submitting'}
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {formState === 'submitting' ? 'Sending...' : 'Submit'}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={formState === 'submitting'}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}

const ContactOverlay = () => {
  const { state, setState, initialData } = useContactOverlay()

  return (
    <Dialog open={state === "open"} onOpenChange={(open) => setState(open ? "open" : "closed")}>
      <DialogContent className="w-full max-w-[600px] mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Contact Us
          </DialogTitle>
          <DialogDescription>
            Get in touch with our support team. We're here to help!
          </DialogDescription>
        </DialogHeader>
        
        <ContactForm 
          onClose={() => setState("closed")} 
          initialData={initialData || undefined}
        />
      </DialogContent>
    </Dialog>
  )
}

// Export all components and hooks
export { ContactOverlayProvider, ContactOverlay, useContactOverlay }
